import { useState } from "react";
import { Upload, Globe, Loader2, Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import EditableField from "@/components/EditableField";
import { BRAND_SWATCHES, FONT_PRESETS, hslTripleToHex, toHslTriple, PLATFORM_DEFAULT } from "@/lib/tenant-theme";

/**
 * Branding editor shared by the signup wizard and AdminBranding. Edits a
 * `branding` object shaped exactly like tenantBrandingSchema
 * (logoUrl/faviconUrl/backgroundImageUrl, colors.{primary,secondary,accent,
 * background,foreground}, fonts.{heading,sans}) so what is edited here is
 * exactly what gets written to Auth config and reused by Talent/Hub.
 *
 * Image inputs stage a data: URL locally when no uploader is provided
 * (public signup has no auth token to call the upload route). When
 * AdminBranding passes `onUploadImage` (authenticated OWN/TSA token), the
 * picker instead POSTs the file to the real S3-backed upload route and
 * stores the returned public URL — the data: URL path is signup-only. SVG
 * is excluded — the Auth upload route rejects it as an XSS vector, so this
 * picker must not accept it either.
 */
const COLOR_FIELDS = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Accent" },
  { key: "background", label: "Background" },
];

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/x-icon", "image/vnd.microsoft.icon"];

/** Shared validation for both the data-URL (signup) and real-upload
 *  (authenticated) paths. Returns true when the file passes. Mirrors the
 *  Auth upload route's own ALLOWED_CONTENT_TYPES (png/jpeg/webp/ico, no svg)
 *  plus a 2MB client-side cap. */
function validateImageFile(file, onError) {
  if (!file) return false;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    onError?.("Only PNG, JPG, WEBP or ICO images are allowed.");
    return false;
  }
  if (file.size > 2 * 1024 * 1024) {
    onError?.("Image must be 2MB or smaller.");
    return false;
  }
  return true;
}

/** Signup-only fallback: read the picked file into a data: URL. */
function readImageFile(file, onLoad, onError) {
  if (!validateImageFile(file, onError)) return;
  const reader = new FileReader();
  reader.onload = () => onLoad(reader.result);
  reader.readAsDataURL(file);
}

function ImageDrop({ id, kind, label, hint, value, onChange, onError, onUploadImage }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!validateImageFile(file, onError)) return;
    if (onUploadImage) {
      // Authenticated path: upload to the real S3-backed route, store the
      // returned public URL (NOT a data: URL, which fails the backend's
      // logoUrl: z.string().url() and never reaches S3).
      setUploading(true);
      try {
        const url = await onUploadImage(kind, file);
        if (url) onChange(url);
      } catch (err) {
        onError?.(typeof err === "string" ? err : err?.message || "Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    } else {
      // Signup path: no auth token yet — stage a data: URL locally.
      readImageFile(file, onChange, onError);
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <label
        className="mt-1.5 flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-input text-xs text-muted-foreground hover:bg-accent/40"
        data-testid={`branding-${id}-upload`}
      >
        {uploading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : value ? (
          <img src={value} alt={`${label} preview`} className="h-12 object-contain" />
        ) : (
          <>
            <Upload className="h-4 w-4" />
            <span>Click to upload</span>
            <span className="text-[10px]">{hint}</span>
          </>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/x-icon"
          className="hidden"
          disabled={uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
    </div>
  );
}

export default function BrandingFields({ branding, onChange, onError, companyName = "", website = null, websiteStatus = "idle", onPullFromWebsite = null, onUploadImage = null }) {
  const colors = branding.colors || {};
  const setColor = (key, raw) => {
    const triple = toHslTriple(raw);
    if (triple === null && raw) {
      onError?.(`Invalid color for ${key}. Use a hex (#2563eb) or "H S% L%".`);
      return;
    }
    onChange({ ...branding, colors: { ...colors, [key]: triple || "" } });
  };
  const activeSwatch = BRAND_SWATCHES.find((s) => s.primary === colors.primary)?.id;
  const activeFont = FONT_PRESETS.find((f) => f.heading === branding.fonts?.heading)?.id || "modern";

  return (
    <div className="space-y-6">
      {(website || websiteStatus !== "idle") && (
        <div className="flex items-center justify-between rounded-md border border-border bg-accent/30 px-3 py-2 text-sm" data-testid="website-palette-banner">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            {websiteStatus === "detecting" && <span className="flex items-center gap-1.5 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading brand colors from {website}…</span>}
            {websiteStatus === "detected" && <span className="flex items-center gap-1.5 text-foreground"><Check className="h-3.5 w-3.5 text-emerald-600" /> Colors detected from <span className="font-medium">{website}</span> — adjust below if needed.</span>}
            {websiteStatus === "notfound" && <span className="text-muted-foreground">Couldn't read colors from {website}; pick a palette below.</span>}
            {websiteStatus === "idle" && website && <span className="text-muted-foreground">Pull brand colors from {website}.</span>}
          </div>
          {onPullFromWebsite && website && websiteStatus !== "detecting" && (
            <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5" onClick={onPullFromWebsite} data-testid="pull-website-colors-btn">
              <Globe className="h-3.5 w-3.5" /> {websiteStatus === "detected" ? "Re-pull" : "Pull colors"}
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <ImageDrop id="logo" kind="logo" label="Logo" hint="PNG, JPG or WEBP (max 2MB)" value={branding.logoUrl}
          onChange={(url) => onChange({ ...branding, logoUrl: url })} onError={onError} onUploadImage={onUploadImage} />
        <ImageDrop id="favicon" kind="favicon" label="Favicon" hint="PNG or ICO (max 1MB)" value={branding.faviconUrl}
          onChange={(url) => onChange({ ...branding, faviconUrl: url })} onError={onError} onUploadImage={onUploadImage} />
        <ImageDrop id="background" kind="background" label="Background" hint="PNG or JPG (login hero)" value={branding.backgroundImageUrl}
          onChange={(url) => onChange({ ...branding, backgroundImageUrl: url })} onError={onError} onUploadImage={onUploadImage} />
      </div>

      <EditableField
        label="Display name"
        value={branding.shortName || ""}
        onChange={(v) => onChange({ ...branding, shortName: v })}
        placeholder={companyName || "Your brand name"}
        emptyText={companyName ? `Using "${companyName}" — click to customize` : "Click to add a display name"}
        testId="branding-short-name"
      />

      <div>
        <Label>Brand palette</Label>
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="brand-palette-note">
          This is your shared brand — both the Talent and Hub portals use it by default. To give a
          portal its own colors, open <span className="font-medium text-foreground">Portals &amp; Modules</span> and
          set that portal's colors there.
        </p>
        {websiteStatus === "detected" ? (
          <div className="mt-2 flex items-center gap-3 rounded-md border border-border bg-background p-3" data-testid="website-palette">
            <div className="flex items-center gap-1.5">
              {["primary", "secondary", "accent"].map((k) => (
                <span key={k} title={k} className="h-8 w-8 rounded-full border border-border" style={{ backgroundColor: `hsl(${colors[k] || PLATFORM_DEFAULT.colors[k]})` }} />
              ))}
            </div>
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground">From {website}</p>
              <p>Pulled from the tenant's website. Fine-tune any color below.</p>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {website ? "Or pick a starting preset:" : "Pick a starting palette (or enter a company domain in step 1 to pull brand colors from the website):"}
            </p>
            <div className="mt-2 flex gap-2" data-testid="branding-color-swatches">
              {BRAND_SWATCHES.map((sw) => (
                <button
                  key={sw.id}
                  type="button"
                  aria-label={`Palette ${sw.id}`}
                  data-testid={`swatch-${sw.id}`}
                  className={`h-9 w-9 rounded-full border-2 ${activeSwatch === sw.id ? "border-foreground" : "border-transparent"}`}
                  style={{ backgroundColor: `hsl(${sw.primary})` }}
                  onClick={() => onChange({ ...branding, colors: { ...colors, primary: sw.primary, secondary: sw.secondary, accent: sw.accent } })}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <Label htmlFor={`color-${key}`}>{label} color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="color"
                aria-label={`${label} color picker`}
                data-testid={`color-picker-${key}`}
                className="h-9 w-10 cursor-pointer rounded border border-input bg-background"
                value={hslTripleToHex(colors[key] || PLATFORM_DEFAULT.colors[key])}
                onChange={(e) => setColor(key, e.target.value)}
              />
              <Input
                id={`color-${key}`}
                data-testid={`branding-${key}-color`}
                className="flex-1"
                placeholder="221 83% 53%"
                value={colors[key] || ""}
                onChange={(e) => setColor(key, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <div>
        <Label htmlFor="typography">Typography</Label>
        <Select
          value={activeFont}
          onValueChange={(id) => {
            const preset = FONT_PRESETS.find((f) => f.id === id);
            if (preset) onChange({ ...branding, fonts: { ...branding.fonts, heading: preset.heading, sans: preset.sans } });
          }}
        >
          <SelectTrigger id="typography" className="mt-1.5" data-testid="branding-typography">
            <SelectValue placeholder="Choose a font" />
          </SelectTrigger>
          <SelectContent>
            {FONT_PRESETS.map((f) => (
              <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-2 rounded-md border border-border bg-muted px-3 py-2 text-sm" style={{ fontFamily: branding.fonts?.heading }}>
          Aa — The quick brown fox jumps over the lazy dog.
        </p>
      </div>
    </div>
  );
}
